import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendingUp, Compass, Leaf, Car, Briefcase } from 'lucide-react';
import './LearnMorePanel.css';

export default function LearnMorePanel({ isOpen, onClose, destination }) {
    const [activeTab, setActiveTab] = useState('overview');

    if (!isOpen || !destination || !destination.intelligence) return null;

    const intel = destination.intelligence;

    const tabs = [
        { id: 'overview', label: 'Overview', icon: Compass },
        { id: 'business', label: 'Business', icon: Briefcase },
        { id: 'culture', label: 'Culture', icon: TrendingUp },
        { id: 'mobility', label: 'Mobility', icon: Car },
        { id: 'environment', label: 'Environment', icon: Leaf }
    ];

    const renderTabContent = () => {
        switch (activeTab) {
            case 'overview':
                return (
                    <div className="lm-grid">
                        <div className="lm-stat"><label>Climate</label><span>{intel.overview.climate}</span></div>
                        <div className="lm-stat"><label>Currency</label><span>{intel.overview.currency}</span></div>
                        <div className="lm-stat"><label>Safety</label><span>{intel.overview.safety}</span></div>
                        <div className="lm-stat"><label>Tourism Focus</label><span>{intel.overview.tourism}</span></div>
                        <div className="lm-stat full-width insight-card">
                            <span className="lm-badge">AI SUMMARY</span>
                            <p>{intel.overview.aiSummary}</p>
                        </div>
                    </div>
                );
            case 'business':
                return (
                    <div className="lm-grid">
                        <div className="lm-stat"><label>GDP / Economy</label><span>{intel.business.gdp}</span></div>
                        <div className="lm-stat"><label>Core Industries</label><span>{intel.business.industries}</span></div>
                        <div className="lm-stat"><label>Cost of Living</label><span>{intel.business.costOfLiving}</span></div>
                        <div className="lm-stat"><label>Avg Internet Speed</label><span>{intel.business.internetSpeed}</span></div>
                        <div className="lm-stat"><label>Startup Ecosystem</label><span>{intel.business.startupEcosystem}</span></div>
                        <div className="lm-stat"><label>Coworking Availability</label><span>{intel.business.coworking}</span></div>
                        <div className="lm-stat full-width insight-card">
                            <span className="lm-badge">AI RECOMMENDATION</span>
                            <p>{intel.business.aiRecommendation}</p>
                        </div>
                    </div>
                );
            case 'culture':
                return (
                    <div className="lm-grid">
                        <div className="lm-stat"><label>Notable Cuisine</label><span>{intel.culture.cuisine}</span></div>
                        <div className="lm-stat"><label>Major Festival</label><span>{intel.culture.festivals}</span></div>
                        <div className="lm-stat"><label>Historical Note</label><span>{intel.culture.history}</span></div>
                        <div className="lm-stat"><label>Primary Language</label><span>{intel.culture.language}</span></div>
                        <div className="lm-stat"><label>Religion</label><span>{intel.culture.religion}</span></div>
                        <div className="lm-stat"><label>Customs</label><span>{intel.culture.customs}</span></div>
                        <div className="lm-stat full-width insight-card">
                            <span className="lm-badge">LOCAL INSIGHT</span>
                            <p>{intel.culture.localInsight}</p>
                        </div>
                    </div>
                );
            case 'mobility':
                return (
                    <div className="lm-grid">
                        <div className="lm-stat"><label>Primary Airport</label><span>{intel.mobility.airport}</span></div>
                        <div className="lm-stat"><label>Public Transit</label><span>{intel.mobility.transit}</span></div>
                        <div className="lm-stat"><label>Walkability</label><span>{intel.mobility.walkability}</span></div>
                        <div className="lm-stat"><label>Bike Infrastructure</label><span>{intel.mobility.bikeScore}</span></div>
                        <div className="lm-stat"><label>Road Network</label><span>{intel.mobility.roadNetwork}</span></div>
                        <div className="lm-stat"><label>EV Charging</label><span>{intel.mobility.evCharging}</span></div>
                    </div>
                );
            case 'environment':
                return (
                    <div className="lm-grid">
                        <div className="lm-stat"><label>Climate Type</label><span>{intel.environment.climate}</span></div>
                        <div className="lm-stat"><label>Terrain</label><span>{intel.environment.terrain}</span></div>
                        <div className="lm-stat"><label>Natural Attractions</label><span>{intel.environment.naturalAttractions}</span></div>
                        <div className="lm-stat"><label>Parks / Reserves</label><span>{intel.environment.parks}</span></div>
                        <div className="lm-stat"><label>Weather Dynamics</label><span>{intel.environment.weather}</span></div>
                        <div className="lm-stat full-width insight-card">
                            <span className="lm-badge">ENVIRONMENTAL NOTES</span>
                            <p>{intel.environment.environmentalNotes}</p>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="lm-overlay"
                        onClick={onClose}
                    />
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="lm-panel glassmorphic-panel"
                    >
                        <div className="lm-header">
                            <div>
                                <span className="lm-pretitle">SPATIAL INTELLIGENCE BRIEF</span>
                                <h2>{destination.title}</h2>
                                <span className="lm-country">{destination.country}</span>
                            </div>
                            <button className="lm-close" onClick={onClose} aria-label="Close panel">
                                <X size={24} color="white" />
                            </button>
                        </div>

                        <div className="lm-tabs">
                            {tabs.map(tab => {
                                const Icon = tab.icon;
                                return (
                                    <button 
                                        key={tab.id}
                                        className={`lm-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                                        onClick={() => setActiveTab(tab.id)}
                                    >
                                        <Icon size={14} />
                                        <span>{tab.label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="lm-content">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={activeTab}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    {renderTabContent()}
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
